import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { Switch, Tooltip } from 'rizzui';
import { toast } from 'sonner';
import DeletePopover from '../../../components/buttons/DeletePopover';
import TabNav, { MenuItem } from "../../../components/navigation/TabNav";
import { usePrivilege } from '../../../components/priviledge/PriviledgeProvider';
import useFetch from '../../../hooks/useFetch';
import useHandleErrors from '../../../hooks/useHandleErrors';
import PageHeader from "../../../layout/shared/page-header";
import { RootState } from '../../../redux/store';
import { setSelectedView } from '../../../redux/productProfileSlice';
import { menuRoutes } from "../../../router/menu";
import CustomSpinner from '../../../loader/CustomSpinner';
import { ProductApiResponse } from '../../../type/entities/product-type';
import { ProductService } from '../../../services/product/productService';

const menuItems: MenuItem[] = [
    {
        label: 'Información',
        path: menuRoutes.products.profile.info,
        permission: {
            group: 'product',
            action: 'get',
        },
    },
    {
        label: 'Movimientos de Stock',
        path: menuRoutes.products.profile.stockMovements,
        permission: {
            group: 'stock_movements',
            action: 'get',
        },
    },
];

export default function ProductProfileLayout() {

    // STATES

    const [changingStatus, setChangingStatus] = useState<string[]>([]);

    // HOOKS

    const { selectedView } = useSelector((state: RootState) => state.productProfile);
    const { user } = useSelector((state: RootState) => state.auth);
    const { id = '' } = useParams<{ id: string }>();
    const { handleErrors } = useHandleErrors();
    const { userCan } = usePrivilege();
    const navigate = useNavigate();
    const service = new ProductService();

    // FUNCTIONS

    //--------------------------------------------------------------------------------------------------------------
    /**
     * @ES OBTENER LOS DATOS DEL PRODUCTO
     * @EN GET PRODUCT DATA
     */
    //--------------------------------------------------------------------------------------------------------------
    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        if (!id || id === '') return null;
        const response = await service.getProductById(id);
        return response.getResponseData() as ProductApiResponse;
    }, [id]));
    //--------------------------------------------------------------------------------------------------------------

    const pageHeader = {
        title: `Datos de ${data?.name || 'Producto'}`,
        breadcrumb: [
            { name: 'Administración' },
            { name: 'Productos', href: menuRoutes.products.path },
            { name: 'Perfil de Producto' },
        ],
    };

    //--------------------------------------------------------------------------------------------------------------
    /**
     * @ES MANEJAR EL CAMBIO DE ESTADO DEL USUARIO
     * @EN HANDLE THE CHANGE OF USER STATUS
     */
    //--------------------------------------------------------------------------------------------------------------
    const toggleStatus = async (id: string, status: boolean) => {
        try {
            setChangingStatus([...changingStatus, id]);
            const response = (await service.toggleProductStatus(id, status)).getResponseData();
            if (response.success) {
                setChangingStatus(changingStatus.filter((item) => item !== id));
                refetch();
                toast.success(status ? "Se ha desactivado el producto" : "Se ha activado el producto"); 
            } else {
                setChangingStatus(changingStatus.filter((item) => item !== id));
                toast.error(response.message);
            }
        } catch (error: any) {
            setChangingStatus(changingStatus.filter((item) => item !== id));
            toast.error(error.message);
        }
    };
    //--------------------------------------------------------------------------------------------------------------

    //--------------------------------------------------------------------------------------------------------------
    /**
     * @ES MANEJAR EL BORRADO DEL PRODUCTO
     * @EN HANDLE THE DELETION OF THE PRODUCT
     */
    //--------------------------------------------------------------------------------------------------------------
    const handleDelete = async (id: string) => {
        try {
            const response = await (await service.deleteProduct(id)).getResponseData();
            if (response.success) {
                navigate(menuRoutes.products.path);
                toast.success('Se ha eliminado correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //--------------------------------------------------------------------------------------------------------------

    // USE EFFECTS

    useEffect(() => {
        navigate(`${menuRoutes.products.path}/${id}/profile/${selectedView}`);
    }, [selectedView]);

    // RENDER

    return (
        <>
            <PageHeader title={pageHeader.title} breadcrumb={pageHeader.breadcrumb}>
                <div className='flex flex-row flex-nowrap gap-3 mt-3 md:mt-0'>
                    {userCan('edit', 'product') && (
                        <>
                            {data && (
                                <>
                                    <Tooltip
                                        size="sm"
                                        content={data.active ? 'Desactivar producto' : 'Activar producto'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <Switch
                                            id={id}
                                            label={data.active ? 'Activo' : 'Desactivado'}
                                            checked={data.active}
                                            onChange={() => toggleStatus(data.id, data.active)}
                                            switchKnobClassName='bg-secondary'
                                        />
                                    </Tooltip>
                                    
                                    {/* <div className='border-l border-gray-300' /> */}

                                </>


                            )}
                        </>
                    )}
                    {/* {userCan('delete', 'product') && (data && data.id !== user?.id) && (
                        <DeletePopover
                            title={`Eliminar producto`}
                            description={`¿Estás seguro de que deseas eliminar el producto?`}
                            size={'md'}
                            onDelete={() => handleDelete(id)}
                            actionIconClassName='h-auto w-10'
                        />
                    )} */}
                </div>
            </PageHeader>

            <TabNav menuItems={menuItems} setSelectedView={setSelectedView} />

            {loading ? <CustomSpinner /> : <Outlet />}
        </>
    );
}